import { ArrowLeft, Camera, Heart, Sparkles, Eye, MapPin, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function PhotographyAdventureGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-cyan-700 hover:text-cyan-800 hover:bg-cyan-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-cyan-100 rounded-full">
              <Camera className="w-8 h-8 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Photography Adventure</h1>
              <p className="text-cyan-600">Week 11, Day 6 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-cyan max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Take a 30-minute walk with your phone camera and photograph 10 things that catch your eye. 
              Look for textures, colors, shadows, and small details most people miss.
            </p>

            <div className="bg-cyan-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-cyan-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Photography is Powerful
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Combines creativity, mindfulness, and movement</li>
                <li>• Trains you to notice beauty in everyday life</li>
                <li>• Preserves moments and memories</li>
                <li>• Cultivates present-moment awareness</li>
                <li>• Requires no special equipment - your phone works</li>
                <li>• Creates a visual record of your unique perspective</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Photo Challenge: 10 Things to Capture</h2>
            
            <div className="grid md:grid-cols-2 gap-3 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-gray-700">An interesting shadow</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-gray-700">A compelling texture</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-gray-700">Your favorite color</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <span className="text-gray-700">Something small most people miss</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">5</div>
                <span className="text-gray-700">A pattern or repetition</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">6</div>
                <span className="text-gray-700">Something that makes you smile</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">7</div>
                <span className="text-gray-700">Light playing on something</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">8</div>
                <span className="text-gray-700">Something old or weathered</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">9</div>
                <span className="text-gray-700">An unexpected detail</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">10</div>
                <span className="text-gray-700">Something beautiful to you</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-100 to-blue-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-cyan-800 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Tips for Better Phone Photos
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Get close:</strong> Fill the frame with your subject</li>
                <li>• <strong>Change angles:</strong> Crouch down, look up, get sideways</li>
                <li>• <strong>Use natural light:</strong> Avoid harsh midday sun when possible</li>
                <li>• <strong>Clean your lens:</strong> Wipe it with your shirt before shooting</li>
                <li>• <strong>Tap to focus:</strong> Touch the screen where you want sharp focus</li>
                <li>• <strong>Rule of thirds:</strong> Put interesting things off-center</li>
                <li>• <strong>Take multiples:</strong> Snap several, pick the best later</li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                After Your Walk
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Review your photos and pick your 3 favorites</li>
                <li>• Try editing them with your phone's built-in editor</li>
                <li>• Consider sharing one on social media or with a friend</li>
                <li>• Reflect on what you noticed that you normally wouldn't</li>
                <li>• Save your favorites to a "Beauty I Found" album</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
